import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Button,
  Space,
  Statistic,
  Tag,
  Empty,
  Timeline,
  Badge,
  Breadcrumb,
  Skeleton,
  Avatar,
  Divider,
  Grid,
  Tabs,
  Progress,
  Alert,
  Tooltip
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  WalletOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TransactionOutlined,
  ReloadOutlined,
  DownloadOutlined,
  HomeOutlined,
  LineChartOutlined,
  BarChartOutlined,
  PieChartOutlined,
  MoneyCollectOutlined,
  DollarOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  SortAscendingOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getRequest } from '../../../Utils/AppApi';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

dayjs.extend(relativeTime);

const UserLedgerDetailsPage = ({ history }) => {
  const { userId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [dateWiseSummary, setDateWiseSummary] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayTransactions, setDayTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [activeTab, setActiveTab] = useState('daily');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Set default date range to last 90 days
  const defaultStartDate = dayjs().subtract(90, 'days').format('DD MMM YYYY');
  const defaultEndDate = dayjs().format('DD MMM YYYY');

  // Debug: Log the userId from URL
  useEffect(() => {
    console.log('User ID from URL params:', userId);
    console.log('Location state:', location.state);
  }, [userId, location.state]);

  // Fetch user ledger details
  const fetchUserLedgerDetails = useCallback(async () => {
    if (!userId) {
      toast.error('User ID is missing');
      history.push('/users');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        userId: userId,
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        startDate: dayjs().subtract(90, 'days').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD')
      });

      const response = await getRequest(`/admin/user-ledger?${params.toString()}`, null, history);
      
      if (response?.success) {
        setUserInfo(response.data.userInfo);
        setLedgerData(response.data.transactions || []);
        setDateWiseSummary(response.data.dateWiseSummary || []);
        setWalletBalance(response.data.walletBalance || 0);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 1
        }));
      } else {
        toast.error(response?.message || 'Failed to load ledger details');
      }
    } catch (error) {
      console.error('Error fetching ledger details:', error);
      toast.error('Error loading ledger details');
    } finally {
      setLoading(false);
    }
  }, [userId, pagination.page, pagination.limit, history]);

  // Calculate summary statistics
  const calculateStats = useCallback(() => {
    if (dateWiseSummary.length === 0) return {};
    
    const totalCredit = dateWiseSummary.reduce((sum, day) => sum + (day.totalCredit || 0), 0);
    const totalDebit = dateWiseSummary.reduce((sum, day) => sum + (day.totalDebit || 0), 0);
    const netFlow = totalCredit - totalDebit;
    const averageDailyBalance = dateWiseSummary.reduce((sum, day) => sum + (day.closingBalance || 0), 0) / dateWiseSummary.length;
    const maxBalance = Math.max(...dateWiseSummary.map(day => day.closingBalance || 0));
    const minBalance = Math.min(...dateWiseSummary.map(day => day.openingBalance || 0));
    
    return {
      totalCredit,
      totalDebit,
      netFlow,
      averageDailyBalance,
      maxBalance,
      minBalance,
      totalTransactions: pagination.total,
      activeDays: dateWiseSummary.length
    };
  }, [dateWiseSummary, pagination.total]);

  const stats = calculateStats();

  // Handle pagination change
  const handlePageChange = (page, pageSize) => {
    setPagination({
      page,
      limit: pageSize,
      total: pagination.total,
      pages: pagination.pages
    });
  };

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    if (userId) {
      fetchUserLedgerDetails();
    }
  }, [userId, fetchUserLedgerDetails]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format currency with decimals
  const formatCurrencyDetailed = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Date-wise summary columns
  const dateSummaryColumns = [
    {
      title: (
        <Space>
          <CalendarOutlined /> Date
          <Button
            type="text"
            icon={<SortAscendingOutlined />}
            size="small"
            onClick={() => setSortConfig({
              key: 'date',
              direction: sortConfig.key === 'date' && sortConfig.direction === 'desc' ? 'asc' : 'desc'
            })}
          />
        </Space>
      ),
      dataIndex: 'date',
      key: 'date',
      render: (date) => (
        <div style={{ padding: '8px 0' }}>
          <Text strong>{date}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(date).format('dddd')}
          </Text>
        </div>
      )
    },
    {
      title: 'Opening',
      dataIndex: 'openingBalance',
      key: 'openingBalance',
      render: (amount) => (
        <div style={{ textAlign: 'right' }}>
          <Text strong style={{ color: '#1890ff', fontSize: 14 }}>
            {formatCurrencyDetailed(amount)}
          </Text>
        </div>
      )
    },
    {
      title: 'Closing',
      dataIndex: 'closingBalance',
      key: 'closingBalance',
      render: (amount) => (
        <div style={{ textAlign: 'right' }}>
          <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
            {formatCurrencyDetailed(amount)}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {amount > 0 ? '▲' : '▼'} {Math.abs(((amount - (dateWiseSummary.find(d => d.date === amount.date)?.openingBalance || amount)) / (amount || 1) * 100)).toFixed(1)}%
          </Text>
        </div>
      )
    },
    {
      title: 'Flow',
      key: 'flow',
      render: (_, record) => {
        const flow = (record.totalCredit || 0) - (record.totalDebit || 0);
        return (
          <div style={{ textAlign: 'center' }}>
            <Tag 
              color={flow >= 0 ? 'green' : 'red'}
              style={{ 
                borderRadius: '12px',
                padding: '4px 12px',
                fontWeight: 500
              }}
            >
              {flow >= 0 ? '+' : ''}{formatCurrencyDetailed(flow)}
            </Tag>
          </div>
        );
      }
    },
    {
      title: 'Transactions',
      dataIndex: 'totalTransactions',
      key: 'totalTransactions',
      render: (count) => (
        <div style={{ textAlign: 'center' }}>
          <Badge 
            count={count} 
            style={{ 
              backgroundColor: count > 10 ? '#1890ff' : '#8c8c8c',
              fontSize: '12px'
            }}
          />
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setSelectedDate(record.date);
            // You can implement day-specific view here
            toast.info(`Viewing transactions for ${record.date}`);
          }}
        >
          View Details
        </Button>
      )
    }
  ];

  // Sort date summary
  const sortedDateSummary = [...dateWiseSummary].sort((a, b) => {
    if (sortConfig.key === 'date') {
      if (sortConfig.direction === 'asc') {
        return dayjs(a.date).diff(dayjs(b.date));
      }
      return dayjs(b.date).diff(dayjs(a.date));
    }
    return 0;
  });

  // Ledger transaction columns
  const ledgerColumns = [
    {
      title: 'Transaction',
      key: 'transaction',
      width: 200,
      render: (record) => (
        <div>
          <Text code style={{ fontSize: 12, background: '#f0f2f5' }}>
            {record.transactionId}
          </Text>
          <div style={{ marginTop: 4 }}>
            <Text strong style={{ fontSize: 14 }}>
              {record.serviceName}
            </Text>
            {record.product && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {record.product}
              </Text>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'time',
      width: 120,
      render: (date) => (
        <div style={{ textAlign: 'center' }}>
          <Text style={{ fontSize: 13 }}>
            {dayjs(date).format('DD MMM')}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs(date).format('hh:mm A')}
          </Text>
        </div>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <Text style={{ fontSize: 13 }}>{text}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      align: 'right',
      width: 120,
      render: (amount) => amount > 0 ? (
        <div style={{ textAlign: 'right' }}>
          <Text strong type="danger" style={{ fontSize: 14 }}>
            -{formatCurrencyDetailed(amount)}
          </Text>
        </div>
      ) : null
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      align: 'right',
      width: 120,
      render: (amount) => amount > 0 ? (
        <div style={{ textAlign: 'right' }}>
          <Text strong type="success" style={{ fontSize: 14 }}>
            +{formatCurrencyDetailed(amount)}
          </Text>
        </div>
      ) : null
    },
    {
      title: 'Balance',
      key: 'balance',
      align: 'right',
      width: 150,
      render: (record) => (
        <div style={{ textAlign: 'right' }}>
          <Space direction="vertical" size={0}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Open: {formatCurrencyDetailed(record.openingBalance)}
            </Text>
            <Text strong style={{ fontSize: 13 }}>
              Close: {formatCurrencyDetailed(record.closingBalance)}
            </Text>
          </Space>
        </div>
      )
    }
  ];

  if (loading && !userInfo) {
    return (
      <div style={{ 
        padding: 24,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh'
      }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <div style={{ 
      padding: isMobile ? 16 : 24,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      {/* Breadcrumb Navigation */}
      <div style={{ marginBottom: 24 }}>
        <Breadcrumb
          items={[
            {
              title: <HomeOutlined />,
              href: '/dashboard'
            },
            {
              title: (
                <Button 
                  type="link" 
                  onClick={() => history.push('/users')}
                  style={{ padding: 0 }}
                >
                  Users
                </Button>
              )
            },
            {
              title: 'Ledger Details',
              style: { color: '#667eea' }
            }
          ]}
          style={{ fontSize: 14 }}
        />
        
        
      </div>

      {/* User Profile Header */}
      <Card
        style={{
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          marginBottom: 24,
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
        bodyStyle={{ padding: isMobile ? 20 : 32 }}
      >
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={6}>
            <Space direction="vertical" align="center" style={{ width: '100%' }}>
              <Avatar
                size={80}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '3px solid white',
                  fontSize: 32,
                  fontWeight: 'bold'
                }}
              >
                {userInfo?.name?.charAt(0)?.toUpperCase() || userInfo?.username?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ color: 'white', margin: '8px 0' }}>
                  {userInfo?.name || 'N/A'}
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                  @{userInfo?.username || 'N/A'}
                </Text>
              </div>
            </Space>
          </Col>
          
          <Col xs={24} md={12}>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Mobile"
                  value={userInfo?.mobile || 'N/A'}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: 'white', fontSize: 14 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="PAN"
                  value={userInfo?.pancard || 'N/A'}
                  prefix={<CreditCardOutlined />}
                  valueStyle={{ color: 'white', fontSize: 14 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Member Since"
                  value={dayjs(userInfo?.createdAt).format('DD MMM YYYY')}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: 'white', fontSize: 14 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Account Status"
                  value={userInfo?.status ? 'Active' : 'Inactive'}
                  valueStyle={{ 
                    color: userInfo?.status ? '#52c41a' : '#ff4d4f',
                    fontSize: 14 
                  }}
                />
              </Col>
            </Row>
          </Col>
          
          <Col xs={24} md={6}>
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <WalletOutlined style={{ fontSize: 32, color: 'white', marginBottom: 8 }} />
              <Title level={3} style={{ color: 'white', margin: '8px 0' }}>
                {formatCurrencyDetailed(walletBalance)}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                Current Balance
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #1890ff, #69c0ff)',
              color: 'white'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Title level={5} style={{ color: 'white', margin: 0 }}>
                  Total Credit
                </Title>
                <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                  {formatCurrency(stats.totalCredit)}
                </Title>
              </div>
              <ArrowUpOutlined style={{ fontSize: 36, color: 'rgba(255,255,255,0.8)' }} />
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #ff4d4f, #ff7875)',
              color: 'white'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Title level={5} style={{ color: 'white', margin: 0 }}>
                  Total Debit
                </Title>
                <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                  {formatCurrency(stats.totalDebit)}
                </Title>
              </div>
              <ArrowDownOutlined style={{ fontSize: 36, color: 'rgba(255,255,255,0.8)' }} />
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #52c41a, #95de64)',
              color: 'white'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Title level={5} style={{ color: 'white', margin: 0 }}>
                  Net Flow
                </Title>
                <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                  {formatCurrency(stats.netFlow)}
                </Title>
              </div>
              <DollarOutlined style={{ fontSize: 36, color: 'rgba(255,255,255,0.8)' }} />
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ 
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #fa8c16, #ffc069)',
              color: 'white'
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Title level={5} style={{ color: 'white', margin: 0 }}>
                  Avg Daily Balance
                </Title>
                <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                  {formatCurrency(stats.averageDailyBalance)}
                </Title>
              </div>
              <LineChartOutlined style={{ fontSize: 36, color: 'rgba(255,255,255,0.8)' }} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Content Card */}
      <Card
        style={{
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: 'none',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Tabs Section */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          style={{ padding: '0 24px', background: '#f8f9fa' }}
          items={[
            {
              key: 'daily',
              label: (
                <Space>
                  <CalendarOutlined />
                  Daily Summary
                  <Badge 
                    count={dateWiseSummary.length} 
                    style={{ backgroundColor: '#1890ff' }}
                  />
                </Space>
              ),
              children: (
                <div style={{ padding: 24 }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 24 
                  }}>
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        <TransactionOutlined /> Daily Balance Summary
                      </Title>
                      <Text type="secondary">
                        {defaultStartDate} - {defaultEndDate}
                      </Text>
                    </div>
                    <Space>
                      <Button icon={<FilterOutlined />}>Filter</Button>
                      <Button icon={<DownloadOutlined />}>Export</Button>
                    </Space>
                  </div>
                  
                  {dateWiseSummary.length > 0 ? (
                    <Table
                      columns={dateSummaryColumns}
                      dataSource={sortedDateSummary}
                      pagination={false}
                      scroll={{ x: 800 }}
                      rowKey="date"
                      style={{ marginBottom: 24 }}
                    />
                  ) : (
                    <Empty description="No ledger data found for the last 90 days" />
                  )}
                </div>
              )
            },
            {
              key: 'transactions',
              label: (
                <Space>
                  <HistoryOutlined />
                  All Transactions
                  <Badge 
                    count={pagination.total} 
                    style={{ backgroundColor: '#1890ff' }}
                  />
                </Space>
              ),
              children: (
                <div style={{ padding: 24 }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 24 
                  }}>
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        <HistoryOutlined /> Transaction History
                      </Title>
                      <Text type="secondary">
                        {pagination.total} transactions in last 90 days
                      </Text>
                    </div>
                    <Space>
                      <Button icon={<FilterOutlined />}>Filter</Button>
                      <Button icon={<DownloadOutlined />}>Export</Button>
                    </Space>
                  </div>
                  
                  {ledgerData.length > 0 ? (
                    <>
                      <Table
                        columns={ledgerColumns}
                        dataSource={ledgerData}
                        pagination={{
                          current: pagination.page,
                          pageSize: pagination.limit,
                          total: pagination.total,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          onChange: handlePageChange,
                          onShowSizeChange: handlePageChange,
                          size: 'default',
                          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`
                        }}
                        scroll={{ x: 1000 }}
                        rowKey="_id"
                      />
                    </>
                  ) : (
                    <Empty description="No transactions found for the last 90 days" />
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* Bottom Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BarChartOutlined />
                Balance Flow Timeline (Last 30 Days)
              </Space>
            }
            style={{ borderRadius: '12px' }}
          >
            {dateWiseSummary.slice(0, 30).length > 0 ? (
              <Timeline mode="left" style={{ marginTop: 16 }}>
                {dateWiseSummary.slice(0, 30).map((day, index) => {
                  const flow = (day.totalCredit || 0) - (day.totalDebit || 0);
                  const isPositive = flow >= 0;
                  
                  return (
                    <Timeline.Item
                      key={day.date}
                      color={isPositive ? 'green' : 'red'}
                      dot={isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      style={{ padding: '8px 0' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>{day.date}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(day.date).format('dddd')}
                          </Text>
                        </div>
                        <Space direction="vertical" align="end" size={0}>
                          <Text strong style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}>
                            {isPositive ? '+' : ''}{formatCurrencyDetailed(flow)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Close: {formatCurrencyDetailed(day.closingBalance)}
                          </Text>
                        </Space>
                      </div>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            ) : (
              <Empty description="No timeline data available for the last 30 days" />
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <PieChartOutlined />
                Performance Insights
              </Space>
            }
            style={{ borderRadius: '12px' }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Progress
                    type="circle"
                    percent={stats.activeDays}
                    format={() => `${stats.activeDays} days`}
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068'
                    }}
                    size={120}
                  />
                  <Text style={{ display: 'block', marginTop: 16 }}>
                    Active Days (out of 90)
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Progress
                    type="circle"
                    percent={Math.round((stats.totalCredit / (stats.totalCredit + stats.totalDebit || 1)) * 100)}
                    format={() => `${Math.round((stats.totalCredit / (stats.totalCredit + stats.totalDebit || 1)) * 100)}%`}
                    strokeColor="#52c41a"
                    size={120}
                  />
                  <Text style={{ display: 'block', marginTop: 16 }}>
                    Credit Ratio
                  </Text>
                </div>
              </Col>
            </Row>
            
            <Divider />
            
            <Row gutter={[16, 16]}>
              <Col xs={12}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                    {formatCurrency(stats.maxBalance)}
                  </Title>
                  <Text type="secondary">Highest Balance</Text>
                </div>
              </Col>
              <Col xs={12}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={5} style={{ margin: 0, color: '#fa8c16' }}>
                    {formatCurrency(stats.minBalance)}
                  </Title>
                  <Text type="secondary">Lowest Balance</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Info Footer */}
      <Alert
        message="Ledger Information"
        description={
          <div>
            <Text>This ledger shows the financial transactions for the user over the last 90 days. </Text>
            <Text strong>Opening Balance</Text> is the balance at the start of each day, and{' '}
            <Text strong>Closing Balance</Text> is the balance at the end of each day after all transactions.
          </div>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ 
          marginTop: 24,
          borderRadius: '8px'
        }}
      />
    </div>
  );
};

export default UserLedgerDetailsPage;